import { useMemo, useState } from "react";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

/**
 * Public pre-registration / waitlist card.
 *
 * Captures UTM/query attribution from the current URL on mount so that
 * ad-driven traffic (Meta / TikTok / X) can be compared later. Persistence
 * happens via POST /api/waitlist/join which is idempotent on email.
 */
export function WaitlistCard() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  // Snapshot attribution ONCE on mount so later navigations do not leak.
  const attribution = useMemo(() => {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const pick = (k) => (params.get(k) || "").slice(0, 128) || undefined;
    return {
      source: "landing_waitlist",
      campaign: pick("campaign"),
      utm_source: pick("utm_source"),
      utm_medium: pick("utm_medium"),
      utm_campaign: pick("utm_campaign"),
      utm_content: pick("utm_content"),
      landing_path: (window.location.pathname + window.location.search).slice(0, 200),
    };
  }, []);

  // Reset transient error when the user edits the email.
  function handleEmailChange(next) {
    setEmail(next);
    if (errorMsg) setErrorMsg("");
    if (status === "error") setStatus("idle");
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;
    if (!consent) {
      setStatus("error");
      setErrorMsg("Devam etmek için lütfen onayı işaretle.");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await api.post("/waitlist/join", {
        email: email.trim().toLowerCase(),
        consent: true,
        ...attribution,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      // On network/429/500 show a safe generic message; on 422 show validation.
      const msg = formatApiError(err) || "Bir sorun oluştu, lütfen tekrar dene.";
      if (err?.response?.status === 429) {
        setErrorMsg("Çok fazla deneme yapıldı. Lütfen kısa süre sonra tekrar dene.");
      } else if (err?.response?.status === 422) {
        setErrorMsg("Lütfen geçerli bir e-posta adresi gir.");
      } else {
        setErrorMsg(msg);
      }
    }
  }

  return (
    <section
      className="border-b border-zinc-200 bg-white"
      data-testid="waitlist-section"
      aria-labelledby="waitlist-heading"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10">
        <div className="rounded-2xl border border-zinc-200 bg-[#FAFAFA] px-5 py-5 lg:px-8 lg:py-6">
          <div className="grid lg:grid-cols-12 gap-4 lg:gap-8 items-center">

            {/* Left: icon + title + description */}
            <div className="lg:col-span-5 flex items-start gap-3">
              <div className="mt-0.5 w-9 h-9 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-[#1E3A8A]" />
              </div>
              <div className="min-w-0">
                <h2
                  id="waitlist-heading"
                  className="font-display text-lg lg:text-xl font-semibold text-zinc-900 tracking-tight leading-snug"
                >
                  SuperZeka açıldığında haber verelim.
                </h2>
                <p className="mt-1 text-sm text-zinc-600 leading-snug">
                  LGS · TYT · AYT sayısal için SuperZeka açıldığında ilk sen haberdar ol.
                </p>
              </div>
            </div>

            {/* Right: form or success */}
            <div className="lg:col-span-7">
              {status === "success" ? (
                <div
                  className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
                  data-testid="waitlist-success"
                  role="status"
                  aria-live="polite"
                >
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Ön kaydın alındı. SuperZeka açıldığında sana haber vereceğiz.
                  </span>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-2" noValidate>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <label className="sr-only" htmlFor="waitlist-email">
                      E-posta adresin
                    </label>
                    <input
                      id="waitlist-email"
                      data-testid="waitlist-email"
                      type="email"
                      autoComplete="email"
                      required
                      inputMode="email"
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder="E-posta adresin"
                      className="flex-1 h-11 rounded-full border border-zinc-300 bg-white px-4 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                    />
                    <Button
                      type="submit"
                      data-testid="waitlist-submit"
                      disabled={status === "submitting"}
                      className="rounded-full h-11 px-6 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm whitespace-nowrap"
                    >
                      {status === "submitting" ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Gönderiliyor…
                        </span>
                      ) : (
                        "Ön Kayıt Ol"
                      )}
                    </Button>
                  </div>

                  <label
                    className="flex items-start gap-2 text-[11px] leading-snug text-zinc-600 cursor-pointer select-none"
                    data-testid="waitlist-consent-label"
                  >
                    <input
                      type="checkbox"
                      data-testid="waitlist-consent"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 w-3.5 h-3.5 rounded border-zinc-300 text-[#1E3A8A] focus:ring-[#1E3A8A]/30"
                    />
                    <span>
                      <a
                        href="/kvkk"
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-zinc-300 hover:decoration-zinc-500"
                      >
                        KVKK Aydınlatma
                      </a>{" "}
                      ve{" "}
                      <a
                        href="/gizlilik"
                        target="_blank"
                        rel="noreferrer"
                        className="underline decoration-zinc-300 hover:decoration-zinc-500"
                      >
                        Gizlilik Politikası
                      </a>
                      &apos;nı okudum; SuperZeka açıldığında bildirim almayı kabul ediyorum. Ücretsizdir; istediğin zaman listeden çıkabilirsin.
                    </span>
                  </label>

                  {status === "error" && errorMsg ? (
                    <div
                      className="text-[11px] text-red-700"
                      data-testid="waitlist-error"
                      role="alert"
                    >
                      {errorMsg}
                    </div>
                  ) : null}
                </form>
              )}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
